import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import InventoryIcon from "src/assets/svg-icons/side-bar/inventory.svg";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import * as yup from "yup";
import { useGetItemById, useGetItemsList } from "src/api/purchasing/items";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { useGetBranches } from "src/api/generic";
import {
  useAddProductionRecipeMutation,
  useGetProRecipeById,
  useUpdateProductionRecipeMutation,
} from "src/api/inventory/production";

const schema = yup.object().shape({
  recipe_quantity: yup.number().required("required"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        item_id: yup.number().required("required"),
        item_name: yup.string(),
        item_sku: yup.string(),
        item_unit: yup.string(),
        item_quantity: yup.number().typeError("Required").required("required"),
      })
    )
    .required("Required"),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const ProductionRecipe = () => {
  const navigate = useNavigate();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { proId, recipeId } = useParams();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const mode = !recipeId ? "add" : "edit";
  console.log({ mode });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const { fields, append, remove } = useFieldArray({
    name: "items",
    control,
  });

  // APIS
  const { data: productionData, isLoading } = useGetItemById({
    enabled: !!proId,
    id: proId as string,
  });

  const {
    data: recipeData,
    status: getRecipeStatus,
    isLoading: isLoadingRecipe,
  } = useGetProRecipeById({
    enabled: !!recipeId,
    id: recipeId as string,
  });

  const { data: itemsData, isFetching: isFetchingItems } = useGetItemsList();
  const { data: branchesData } = useGetBranches();
  const { mutate, error, status, isPending } = useAddProductionRecipeMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateProductionRecipeMutation();

  const navigateToInventory = () => {
    navigate("/inventory/production");
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    setSelectedItem(newValue);
    if (newValue) {
      append({
        item_id: newValue.id,
        item_name: newValue.name,
        item_sku: newValue.sku,
        item_unit: newValue.unit?.name,
        item_quantity: 0,
      });
      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handle manual changes to the input field
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    console.log({ data });
    const { recipe_quantity, items } = data;
    const dataToSend = {
      quantity: recipe_quantity,
      item_id: Number(proId),
      items: items.map((item) => {
        return {
          item_id: item.item_id,
          quantity: item.item_quantity,
        };
      }),
    };
    if (mode === "add") {
      mutate(dataToSend);
    } else {
      mutateUpdate({
        id: recipeId as string,
        ...dataToSend,
      });
    }
  };

  useEffect(() => {
    if (getRecipeStatus === "success") {
      console.log("resetting", { recipeData });
      reset({
        recipe_quantity: recipeData.data?.quantity,
        items: recipeData.data.product_recipe_items.map((item: any) => {
          return {
            item_id: item.item?.id,
            item_name: item.item?.name,
            item_sku: item.item?.sku,
            item_unit: item.item?.unit?.name,
            item_quantity: item.quantity,
            item_unit_cost: item.item?.unit_cost,
          };
        }),
      });
    }
  }, [getRecipeStatus]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
      navigateToInventory();
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  const selectedItemsIds = watch("items")?.map((item) => item.item_id);

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={InventoryIcon} width={"32px"} alt="" />
        </Box>
        <Stack>
          <Typography fontSize={"30px"} lineHeight={"38.4px"}>
            {productionData?.data.name}
          </Typography>
          {/* <Typography lineHeight={"38.4px"}>
            {t("totalCost")}: <strong>{recipeCost.toFixed(3)} SAR</strong>
          </Typography> */}
        </Stack>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("inventory")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInventory}
          sx={{ cursor: "pointer" }}
        >
          {t("production")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {/* {mode === "add" ? generalT("add") : productionData?.data.reference} */}
            {productionData?.data.name}
          </Typography>
        </Box>
      </Stack>

      {isLoading || isLoadingRecipe ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"250px"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={{ xs: 2, md: 10 }}
                border={"1px solid #F0F0F0"}
                borderRadius={"12px"}
                p={2}
              >
                <Typography color={"#667085"}>{t("totalProIs")}</Typography>
                <Controller
                  name={`recipe_quantity`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label={`${t("recipeQuantity")}`}
                      variant="outlined"
                      {...field}
                      error={!!errors?.recipe_quantity}
                      helperText={errors?.recipe_quantity?.message}
                      fullWidth
                      size="small"
                      sx={{ minWidth: 170, width: 170 }}
                    />
                  )}
                />
                <Stack direction={"row"} gap={0.5}>
                  <Typography color={"#667085"}>{t("unit")}:</Typography>
                  <Typography color={"#667085"} fontWeight={600}>
                    {productionData.data?.unit?.name}
                  </Typography>
                </Stack>
              </Stack>

              {fields.map((itemField, index) => {
                return (
                  <Box
                    key={itemField.id}
                    // padding={"14px 16px"}
                    borderRadius={1.5}
                    // border={"2px solid #F0F0F0"}
                    // boxShadow={"rgba(99, 99, 99, 0.2) 0px 2px 8px 0px"}
                  >
                    <Grid
                      container
                      spacing={{ xs: 1, md: 2 }}
                      alignItems={"center"}
                    >
                      <Grid item xs={11} sm={11}>
                        <Stack
                          direction={"row"}
                          gap={{ xs: 2, md: 6.5 }}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                        >
                          <Box sx={{ minWidth: 180, width: 180 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("item")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                            >
                              {itemField.item_name} ({itemField.item_unit})
                            </Typography>
                          </Box>

                          <Box sx={{ minWidth: 80, width: 80 }}>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                            >
                              {t("sku")}
                            </Typography>
                            <Typography
                              color={"#667085"}
                              fontSize={"13px"}
                              lineHeight={"24px"}
                              fontWeight={700}
                              whiteSpace={"nowrap"}
                            >
                              {itemField.item_sku}
                            </Typography>
                          </Box>

                          <Controller
                            name={`items.${index}.item_quantity`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                type="number"
                                label={`${t("qnty")}`}
                                variant="outlined"
                                {...field}
                                error={!!errors.items?.[index]?.item_quantity}
                                helperText={
                                  errors.items?.[index]?.item_quantity?.message
                                }
                                fullWidth
                                size="small"
                                sx={{ minWidth: 170, width: 170 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Typography fontSize={"14px"}>
                                        {itemField.item_unit}
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={1} md={1}>
                        <Box
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                          sx={{ flexGrow: 1 }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleDeleteItem(index);
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt=""
                              width={"16px"}
                              style={{ cursor: "pointer" }}
                            />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
              <Autocomplete
                size="small"
                disablePortal
                id="combo-box-demo"
                options={
                  itemsData?.data.filter(
                    (item: any) =>
                      !selectedItemsIds?.includes(item.id) &&
                      item.id !== Number(proId)
                  ) ?? []
                }
                sx={{ width: 255 }}
                getOptionLabel={(option: any) => option.name}
                getOptionKey={(option) => `${option.id}`}
                value={selectedItem}
                onChange={handleItemSelect}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderInput={(params: any) => (
                  <TextField {...params} label={t("searchAndAddItem")} />
                )}
                loading={isFetchingItems}
                noOptionsText={generalT("noOption")}
              />
            </Stack>

            <Stack
              spacing={2}
              direction={"row"}
              mt={3}
              justifyContent={"flex-end"}
            >
              <Button
                variant="outlined"
                color="tertiary"
                onClick={navigateToInventory}
                sx={{ minWidth: "180px" }}
              >
                {generalT("back")}
              </Button>

              <LoadingButton
                variant="contained"
                type="submit"
                loading={isPendingUpdate || isPending}
                sx={{ minWidth: "180px" }}
                disabled={watch("items")?.length === 0}
              >
                {generalT("save")}
              </LoadingButton>
            </Stack>
          </form>
        </>
      )}
    </Stack>
  );
};

export default ProductionRecipe;
